import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../../config/api';

const IndicadorManagement = () => {
  const navigate = useNavigate();
  const [indicadores, setIndicadores] = useState([]);
  const [searchForm, setSearchForm] = useState({
    code: '',
    cpfCnpj: '',
    name: '',
    representative: '',
    sector: ''
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [tooltipClientId, setTooltipClientId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [indicadorToDelete, setIndicadorToDelete] = useState(null);

  const [showViewModal, setShowViewModal] = useState(false);
  const [indicadorToView, setIndicadorToView] = useState(null);

  // Fetch usuarios from Supabase
  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const response = await fetch(`${API_URL}/api/usuarios`);
        if (!response.ok) {
          throw new Error('Erro ao buscar usuários');
        }
        const data = await response.json();
        setUsuarios(data.usuarios || []);
      } catch (err) {
        console.error('Erro ao carregar usuários:', err);
      }
    };
    fetchUsuarios();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Utility function to format Brazilian phone numbers
  const formatBrazilianPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      // Mobile format: (XX) 9XXXX-XXXX
      return cleaned.replace(/^(\d{2})(\d{1})(\d{4})(\d{4})$/, '($1) $2 $3-$4');
    } else if (cleaned.length === 10) {
      // Landline format: (XX) XXXX-XXXX
      return cleaned.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    } else {
      return phone; // Return as is if length doesn't match
    }
  };

  const fetchIndicadores = async (params) => {
    setLoading(true);
    setError('');
    try {
      const query = new URLSearchParams(params).toString();
      const response = await fetch(`${API_URL}/api/indicador?${query}`);
      if (!response.ok) {
        throw new Error('Erro ao buscar indicadores');
      }
      const data = await response.json();
      const normalizedIndicadores = (data.indicadores || data.indicador || []).map(indicador => ({
        ...indicador,
        representative: indicador.representante || indicador.representative || '',
        sector: indicador.setor || indicador.sector || '',
        address: indicador.endereco || indicador.address || '',
        phone: formatBrazilianPhone(indicador.telefone || indicador.phone || ''),
        celular: formatBrazilianPhone(indicador.celular || indicador.phone || ''),
        cidade: indicador.cidade || '',
        estado: indicador.estado || '',
      }));
      setIndicadores(normalizedIndicadores);
      setHasSearched(true);
    } catch (err) {
      setError(err.message || 'Erro desconhecido');
      setIndicadores([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setError('');
    if (!searchForm.sector) {
      setError('O campo Setor é obrigatório para a busca.');
      setIndicadores([]);
      setHasSearched(false);
      return;
    }
    fetchIndicadores(searchForm);
  };

  const handleDeleteClick = (indicador) => {
    setIndicadorToDelete(indicador);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/api/indicador/${indicadorToDelete.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Erro ao apagar indicador');
      }
      setIndicadores(indicadores.filter(i => i.id !== indicadorToDelete.id));
      setShowDeleteModal(false);
      setIndicadorToDelete(null);
    } catch (error) {
      alert('Erro ao apagar indicador. Por favor, tente novamente.');
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setIndicadorToDelete(null);
  };

  const handleRegisterOption = (option) => {
    setShowDropdown(false);
    if (option === 'blank') {
      navigate('/admin/indicador/register');
    }
  };

  const handleEditClick = (indicador) => {
    navigate(`/admin/indicador/register/${indicador.code}`);
  };

  const handleViewClick = (indicador) => {
    setIndicadorToView(indicador);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setIndicadorToView(null);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold border-l-4 border-purple-600 pl-4">
          Consultar indicador
        </h2>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg shadow-md transition-colors duration-200 flex items-center gap-2"
          >
            Cadastrar indicador
          </button>
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
              <div className="py-1" role="menu" aria-orientation="vertical">
                <button
                  onClick={() => handleRegisterOption('blank')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  role="menuitem"
                >
                  Cadastro em branco
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700">Código do indicador</label>
            <input
              type="text"
              name="code"
              value={searchForm.code}
              onChange={handleSearchChange}
              placeholder="Informe um código"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">CPF/CNPJ</label>
            <input
              type="text"
              name="cpfCnpj"
              value={searchForm.cpfCnpj}
              onChange={handleSearchChange}
              placeholder="Informe CPF/CNPJ"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nome</label>
            <input
              type="text"
              name="name"
              value={searchForm.name}
              onChange={handleSearchChange}
              placeholder="Informe um nome"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Representante</label>
            <select
              name="representative"
              value={searchForm.representative}
              onChange={handleSearchChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
            >
              <option value="">Selecione um representante</option>
              {usuarios.map((usuario) => (
                <option key={usuario.id} value={`${usuario.code} - ${usuario.name || usuario.nome || usuario.username}`}>
                  {usuario.code} - {usuario.name || usuario.nome || usuario.username}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Setor <span className="text-red-600">*</span>
            </label>
            <select
              name="sector"
              value={searchForm.sector}
              onChange={handleSearchChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              required
            >
              <option value="">Selecione um setor</option>
              <option value="Comercial">Comercial</option>
              <option value="Técnico">Técnico</option>
            </select>
          </div>
          <div className="md:col-span-5 flex justify-end mt-2">
            <button
              type="submit"
              className="bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 transition-colors duration-200"
              disabled={loading}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden overflow-x-auto">
        <h3 className="text-lg font-semibold p-4 border-b border-gray-200">Lista de indicadores</h3>
        {error && <p className="text-red-600 p-4">{error}</p>}
        {!error && indicadores.length === 0 && !loading && hasSearched && (
          <p className="p-4 text-gray-500">Nenhum indicador encontrado.</p>
        )}
        {hasSearched && indicadores.length > 0 && (
          <>
            {/* Pagination Controls - Top */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border-b border-gray-200 gap-2">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Itens por página</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="40">40</option>
                  <option value="50">50</option>
                </select>
                <span className="text-sm text-gray-600">
                  {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, indicadores.length)} de {indicadores.length}
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-purple-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Código</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider hidden md:table-cell">CPF/CNPJ</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Nome</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider hidden lg:table-cell">Representante</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider hidden sm:table-cell">Setor</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider hidden xl:table-cell">Telefone</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {indicadores
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((indicador) => (
                      <tr key={indicador.id} className="hover:bg-gray-50">
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">{indicador.code}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm hidden md:table-cell">{indicador.cpfCnpj}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap font-medium text-gray-900 text-sm">{indicador.name}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm hidden lg:table-cell">{indicador.representative}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm hidden sm:table-cell">{indicador.sector}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm hidden xl:table-cell">{indicador.phone}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2 sm:gap-3 relative">
                      <button
                        onMouseEnter={() => {
                          setTooltip('edit');
                          setTooltipClientId(indicador.id);
                        }}
                        onMouseLeave={() => {
                          setTooltip(null);
                          setTooltipClientId(null);
                        }}
                        onClick={() => handleEditClick(indicador)}
                        className="text-blue-600 hover:text-blue-900 relative group"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        {tooltip === 'edit' && tooltipClientId === indicador.id && (
                          <div className="absolute left-1/2 -translate-x-1/2 -top-10 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-50">
                            Editar cadastro do indicador
                            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        )}
                      </button>
                      <button
                        onMouseEnter={() => {
                          setTooltip('view');
                          setTooltipClientId(indicador.id);
                        }}
                        onMouseLeave={() => {
                          setTooltip(null);
                          setTooltipClientId(null);
                        }}
                        onClick={() => handleViewClick(indicador)}
                        className="text-blue-600 hover:text-blue-900 relative group"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                          <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                        </svg>
                        {tooltip === 'view' && tooltipClientId === indicador.id && (
                          <div className="absolute left-1/2 -translate-x-1/2 -top-10 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-50">
                            Visualizar cadastro do indicador
                            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteClick(indicador)}
                        onMouseEnter={() => {
                          setTooltip('delete');
                          setTooltipClientId(indicador.id);
                        }}
                        onMouseLeave={() => {
                          setTooltip(null);
                          setTooltipClientId(null);
                        }}
                        className="text-red-600 hover:text-red-900 relative group"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {tooltip === 'delete' && tooltipClientId === indicador.id && (
                          <div className="absolute left-1/2 -translate-x-1/2 -top-10 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-50">
                            Apagar Cadastro
                            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        )}
                      </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls - Bottom */}
            {Math.ceil(indicadores.length / itemsPerPage) > 1 && (
              <div className="flex flex-wrap justify-center items-center p-4 border-t border-gray-200 gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                
                {[...Array(Math.ceil(indicadores.length / itemsPerPage))].map((_, index) => {
                  const pageNumber = index + 1;
                  const totalPages = Math.ceil(indicadores.length / itemsPerPage);
                  
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-3 py-1 border rounded-md ${
                          currentPage === pageNumber
                            ? 'bg-purple-600 text-white border-purple-600'
                            : 'border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber} className="px-2">...</span>;
                  }
                  return null;
                })}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(indicadores.length / itemsPerPage)))}
                  disabled={currentPage === Math.ceil(indicadores.length / itemsPerPage)}
                  className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showViewModal && indicadorToView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-4 sm:p-6 relative max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">Detalhes do Indicador</h3>
            <button
              onClick={closeViewModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-900"
              aria-label="Fechar"
            >
              &times;
            </button>
            <div className="space-y-2 text-sm">
              <p><strong>Código:</strong> {indicadorToView.code}</p>
              <p><strong>CPF/CNPJ:</strong> {indicadorToView.cpfCnpj}</p>
              <p><strong>Nome:</strong> {indicadorToView.name}</p>
              <p><strong>Representante:</strong> {indicadorToView.representative}</p>
              <p><strong>Setor:</strong> {indicadorToView.sector}</p>
              {/* Add more fields as needed */}
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && indicadorToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-4 sm:p-6 relative">
            <h3 className="text-xl font-semibold mb-4">Confirmar exclusão</h3>
            <p>Tem certeza que deseja excluir o indicador <strong>{indicadorToDelete.name}</strong>?</p>
            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndicadorManagement;
